import { useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

import CustomTextField from './CustomTextField';
import { parseDecimalInput } from '../../../utils/parseDecimalInput';

const WARNING_DURATION_MS = 5000;

/**
 * Campo numérico decimal que acepta punto o coma como separador decimal y guarda siempre punto.
 * Si se escribe más de un separador (miles con punto o coma) no toma el cambio y lo explica en un
 * aviso flotante arriba de la pantalla (no debajo del campo, para que no se corte dentro de
 * tablas).
 * Sin flechas de subir y bajar. Entrega a `onChange` un evento `{ target: { name, value } }`.
 * @param {Object} props
 * @param {string|number} props.value Valor con punto decimal (o vacío).
 * @param {Function} props.onChange Recibe el evento con el valor normalizado.
 * @param {number} [props.decimals] Máximo de decimales (3 cantidades, 2 importes).
 * @param {boolean} [props.error] Error de validación del formulario.
 * @param {string} [props.helperText] Texto de ayuda o error del formulario.
 * @param {Object} [props.inputProps] Props del `input` (por ejemplo `style.textAlign`).
 * @returns {JSX.Element}
 */
const DecimalTextField = ({
  value,
  onChange,
  decimals = 3,
  error = false,
  helperText,
  inputProps = {},
  name,
  ...props
}) => {
  const [warning, setWarning] = useState('');

  const handleChange = (event) => {
    const result = parseDecimalInput(event.target.value, decimals);
    if (result.warning) {
      setWarning(result.warning);
      return;
    }
    setWarning('');
    onChange({ target: { name: name ?? event.target.name, value: result.value } });
  };

  return (
    <>
      <CustomTextField
        {...props}
        name={name}
        type="text"
        value={value ?? ''}
        onChange={handleChange}
        error={Boolean(warning) || error}
        helperText={helperText}
        inputProps={{ inputMode: 'decimal', autoComplete: 'off', ...inputProps }}
      />
      <Snackbar
        open={Boolean(warning)}
        autoHideDuration={WARNING_DURATION_MS}
        onClose={() => setWarning('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setWarning('')} severity="warning" sx={{ width: '100%' }}>
          {warning}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DecimalTextField;
